import React, { useEffect, useState } from 'react';
import { useFlag } from '../../lib/flags';
import * as store from '../../lib/localStore';
import './A11ySettings.css';

// User-controlled accessibility preferences, stored locally. Today: applies
// CSS variables / classes to <html>. Later (when accounts exist): sync to
// user profile so the preferences follow them across devices.

const STORAGE_KEY = 'a11y:prefs';

const DEFAULTS = {
  reduceMotion: false,
  fontScale: 1,        // 1 = normal, 1.125 = larger, 1.25 = largest
  highContrast: false,
};

const FONT_LABELS = [
  { value: 1,     label: 'Standard' },
  { value: 1.125, label: 'Large' },
  { value: 1.25,  label: 'Largest' },
];

const applyPrefs = (prefs) => {
  const html = document.documentElement;
  if (prefs.reduceMotion) html.setAttribute('data-tn-reduce-motion', 'true');
  else html.removeAttribute('data-tn-reduce-motion');
  if (prefs.highContrast) html.setAttribute('data-tn-high-contrast', 'true');
  else html.removeAttribute('data-tn-high-contrast');
  html.style.setProperty('--tn-font-scale', String(prefs.fontScale));
};

const A11ySettings = () => {
  const enabled = useFlag('a11ySettingsPanel');
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState(() => ({ ...DEFAULTS, ...(store.get(STORAGE_KEY) || {}) }));

  // Apply on mount (and whenever prefs change). Runs even when the panel
  // itself is disabled — saved prefs from a previous session still take effect.
  useEffect(() => {
    applyPrefs(prefs);
  }, [prefs]);

  const update = (patch) => {
    const next = { ...prefs, ...patch };
    setPrefs(next);
    store.set(STORAGE_KEY, next);
  };

  const reset = () => {
    setPrefs(DEFAULTS);
    store.remove(STORAGE_KEY);
  };

  if (!enabled) return null;

  return (
    <>
      <button
        type="button"
        className="tn-a11y-toggle"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Accessibility settings"
        onClick={() => setOpen((v) => !v)}
        title="Accessibility settings"
      >
        <span aria-hidden="true">A11y</span>
      </button>

      {open && (
        <div
          className="tn-a11y-panel"
          role="dialog"
          aria-modal="false"
          aria-label="Accessibility settings"
          onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
        >
          <header className="tn-a11y-panel__head">
            <h2>Accessibility</h2>
            <button
              type="button"
              className="tn-a11y-panel__close"
              aria-label="Close"
              onClick={() => setOpen(false)}
            >×</button>
          </header>

          <fieldset className="tn-a11y-panel__group">
            <legend>Motion</legend>
            <label className="tn-a11y-panel__row">
              <input
                type="checkbox"
                checked={prefs.reduceMotion}
                onChange={(e) => update({ reduceMotion: e.target.checked })}
              />
              <span>Reduce animations</span>
            </label>
            <p className="tn-a11y-panel__hint">
              Overrides system preference. We already honour <code>prefers-reduced-motion</code>.
            </p>
          </fieldset>

          <fieldset className="tn-a11y-panel__group">
            <legend>Text size</legend>
            <div className="tn-a11y-panel__seg" role="radiogroup" aria-label="Text size">
              {FONT_LABELS.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={prefs.fontScale === value}
                  className={`tn-a11y-panel__seg-btn ${prefs.fontScale === value ? 'is-active' : ''}`}
                  onClick={() => update({ fontScale: value })}
                >
                  {label}
                </button>
              ))}
            </div>
          </fieldset>

          <fieldset className="tn-a11y-panel__group">
            <legend>Contrast</legend>
            <label className="tn-a11y-panel__row">
              <input
                type="checkbox"
                checked={prefs.highContrast}
                onChange={(e) => update({ highContrast: e.target.checked })}
              />
              <span>Higher contrast</span>
            </label>
            <p className="tn-a11y-panel__hint">
              Increases text contrast on dark backgrounds. Style hooks live on
              <code>html[data-tn-high-contrast]</code>.
            </p>
          </fieldset>

          <button type="button" className="tn-a11y-panel__reset" onClick={reset}>
            Reset to defaults
          </button>
        </div>
      )}
    </>
  );
};

export default A11ySettings;
